import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react3'

import Image from './image'
import {hasValidAsset, hasValidImageSrc} from '../../utils/utils'
import ModuleContainer from '../../components/cms/module-container'
import MediaAnimation from './animation'
import Asset from '../../components/cms/asset'

/**
 * MediaModule component renders media content (images or videos) with optional animation and darkening overlay.
 * Supports both image and video assets with customizable display options.
 *
 * @param {Object} props - The component props
 * @param {string} props.moduleId - Unique identifier for the module
 * @param {string} props.moduleType - Type of the module for categorization
 * @param {string} props.pageId - Identifier of the page containing this module
 * @param {string} [props.anchor] - Optional anchor ID for navigation linking
 * @param {Object} [props.videoConfig] - Configuration object for video playback settings
 * @param {boolean} [props.withAnimation] - Whether to wrap content with animation component
 * @param {string} [props.duration] - Duration string for video content display
 * @param {string} [props.videoName] - Name/title to display for video content
 * @param {Object} [props.asset] - Media asset object containing src, type, and other media properties
 * @param {string} [props.darken] - Percentage string for overlay darkening effect (e.g., "20%")
 * @returns {JSX.Element|null} Rendered media module or null if asset is invalid
 */
const MediaModule = (props) => {
    const {
        moduleId,
        moduleType,
        pageId,
        anchor,
        withAnimation,
        asset,
        darken: darkenPercent
    } = props
    const darken = darkenPercent ? darkenPercent.replace('%', '') : '0'
    const videoRef = useRef(null)

    if (!hasValidAsset(asset)) {
        console.warn(`Asset not supported`)
        return null
    }

    const content = hasValidImageSrc(asset) ? (
        <Image {...props} />
    ) : (
        <Box position="relative">
            <Asset
                variant={props.videoName ? 'videoWithInfo' : 'default'}
                asset={{
                    ...props.asset,
                    autoplay: true,
                    videoName: props.videoName,
                    duration: props.duration
                }}
                videoRef={videoRef}
            />
            <Box
                position="absolute"
                inset={0}
                pointerEvents="none"
                zIndex={1}
                bg={`gradient.bottom.darken-${darken}`}
            ></Box>
        </Box>
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            spacing="none"
            modulePadding="media"
        >
            {withAnimation ? <MediaAnimation>{content}</MediaAnimation> : content}
        </ModuleContainer>
    )
}

MediaModule.propTypes = {
    moduleId: PropTypes.string.isRequired,
    moduleType: PropTypes.string.isRequired,
    pageId: PropTypes.string.isRequired,
    anchor: PropTypes.string,
    videoConfig: PropTypes.object,
    withAnimation: PropTypes.bool,
    duration: PropTypes.string,
    videoName: PropTypes.string,
    asset: PropTypes.object,
    darken: PropTypes.string
}

export default MediaModule
